export { categories, categoryRecord, coreCategories, isDataType, moduleCategories } from '../src/builtin/interface'
export type { Category, DataType } from '../src/builtin/interface'

function getNumberTheorySequenceNames<T extends string>(name: T): [`number-theory.${T}-seq`, `number-theory.${T}-nth`, `number-theory.${T}-take-while`, `number-theory.${T}?`] {
  return [`number-theory.${name}-seq`, `number-theory.${name}-nth`, `number-theory.${name}-take-while`, `number-theory.${name}?`]
}

function getVectorReductionNames<T extends string>(name: T): [`vector.${T}`, `vector.moving-${string}`, `vector.centered-moving-${string}`, `vector.running-${string}`] {
  const baseName = name.replace(/^/, '')
  return [`vector.${name}`, `vector.moving-${baseName}`, `vector.centered-moving-${baseName}`, `vector.running-${baseName}`]
}

function getVectorMovingNames<T extends string>(name: T): [`vector.moving-${string}`, `vector.centered-moving-${string}`, `vector.running-${string}`] {
  return [`vector.moving-${name}`, `vector.centered-moving-${name}`, `vector.running-${name}`]
}

export const api = {
  collection: [
    'filter',
    'map',
    'reduce',
    'count',
    'get',
    'contains?',
    'assoc',
    '++',
  ] as const,
  collectionUtils: [
    'collection.filteri',
    'collection.mapi',
    'collection.reducei',
    'collection.reduce-right',
    'collection.reducei-right',
    'collection.reductions',
    'collection.reductionsi',
    'collection.get-in',
    'collection.assoc-in',
    'collection.update',
    'collection.update-in',
    'collection.not-empty',
    'collection.every?',
    'collection.not-every?',
    'collection.any?',
    'collection.not-any?',
  ] as const,
  array: [
    'range',
    'repeat',
    'flatten',
    'mapcat',
    'moving-fn',
    'running-fn',
  ] as const,
  sequence: [
    'nth',
    'push',
    'pop',
    'index-of',
    'some',
    'reverse',
    'first',
    'second',
    'last',
    'rest',
    'next',
    'sort',
    'slice',
    'take',
    'take-last',
    'drop',
    'drop-last',
    'take-while',
    'drop-while',
  ] as const,
  sequenceUtils: [
    'sequence.position',
    'sequence.last-index-of',
    'sequence.unshift',
    'sequence.splice',
    'sequence.sort-by',
    'sequence.distinct',
    'sequence.remove',
    'sequence.remove-at',
    'sequence.split-at',
    'sequence.split-with',
    'sequence.frequencies',
    'sequence.group-by',
    'sequence.partition',
    'sequence.partition-all',
    'sequence.partition-by',
    'sequence.starts-with?',
    'sequence.ends-with?',
    'sequence.interleave',
    'sequence.interpose',
  ] as const,
  math: [
    '+',
    '-',
    '*',
    '/',
    'mod',
    '%',
    'quot',
    'inc',
    'dec',
    'sqrt',
    'cbrt',
    '^',
    'round',
    'trunc',
    'floor',
    'ceil',
    'min',
    'max',
    'abs',
    'sign',
  ] as const,
  mathUtils: [
    'math.ln',
    'math.log2',
    'math.log10',
    'math.sin',
    'math.cos',
    'math.tan',
    'math.asin',
    'math.acos',
    'math.atan',
    'math.sinh',
    'math.cosh',
    'math.tanh',
    'math.asinh',
    'math.acosh',
    'math.atanh',
    'math.to-rad',
    'math.to-deg',
  ] as const,
  functional: [
    '|>',
    'apply',
    'identity',
    'comp',
    'constantly',
  ] as const,
  functionalUtils: [
    'functional.juxt',
    'functional.complement',
    'functional.every-pred',
    'functional.some-pred',
    'functional.fnull',
  ] as const,
  meta: [
    'doc',
    'arity',
  ] as const,
  misc: [
    '!=',
    '==',
    '<',
    '>',
    '<=',
    '>=',
    'not',
    'write!',
    'iso-date->epoch',
    'epoch->iso-date',
    'boolean',
    'compare',
    'identical?',
    'json-parse',
    'json-stringify',
  ] as const,
  object: [
    'dissoc',
    'keys',
    'vals',
    'entries',
    'find',
    'merge',
    'merge-with',
    'zipmap',
    'select-keys',
  ] as const,
  predicate: [
    'boolean?',
    'null?',
    'number?',
    'string?',
    'function?',
    'integer?',
    'array?',
    'object?',
    'collection?',
    'sequence?',
    'regexp?',
    'zero?',
    'pos?',
    'neg?',
    'even?',
    'odd?',
    'finite?',
    'negative-infinity?',
    'positive-infinity?',
    'false?',
    'true?',
    'empty?',
    'not-empty?',
    'vector?',
    'grid?',
    'matrix?',
  ] as const,
  regularExpression: [
    'regexp',
    're-match',
    'replace',
    'replace-all',
  ] as const,
  string: [
    'str',
    'number',
    'lower-case',
    'upper-case',
    'trim',
    'split',
    'join',
    'blank?',
  ] as const,
  stringUtils: [
    'string.string-repeat',
    'string.from-char-code',
    'string.to-char-code',
    'string.trim-left',
    'string.trim-right',
    'string.pad-left',
    'string.pad-right',
    'string.split-lines',
    'string.template',
    'string.encode-base64',
    'string.decode-base64',
    'string.encode-uri-component',
    'string.decode-uri-component',
    'string.capitalize',
  ] as const,
  bitwise: [
    '<<',
    '>>',
    '>>>',
    '&',
    '|',
    'xor',
  ] as const,
  bitwiseUtils: [
    'bitwise.bit-not',
    'bitwise.bit-and-not',
    'bitwise.bit-flip',
    'bitwise.bit-clear',
    'bitwise.bit-set',
    'bitwise.bit-test',
  ] as const,
  assertion: [
    'assert',
  ] as const,
  assertionUtils: [
    'assertion.assert=',
    'assertion.assert!=',
    'assertion.assert-gt',
    'assertion.assert-lt',
    'assertion.assert-gte',
    'assertion.assert-lte',
    'assertion.assert-true',
    'assertion.assert-false',
    'assertion.assert-truthy',
    'assertion.assert-falsy',
    'assertion.assert-null',
    'assertion.assert-throws',
    'assertion.assert-throws-error',
    'assertion.assert-not-throws',
    'assertion.assert-array',
    'assertion.assert-boolean',
    'assertion.assert-collection',
    'assertion.assert-function',
    'assertion.assert-grid',
    'assertion.assert-integer',
    'assertion.assert-matrix',
    'assertion.assert-number',
    'assertion.assert-object',
    'assertion.assert-regexp',
    'assertion.assert-sequence',
    'assertion.assert-string',
    'assertion.assert-vector',
  ] as const,
  grid: [
    'grid.cell-every?',
    'grid.some?',
    'grid.every-row?',
    'grid.some-row?',
    'grid.every-col?',
    'grid.some-col?',
    'grid.row',
    'grid.col',
    'grid.shape',
    'grid.fill',
    'grid.generate',
    'grid.reshape',
    'grid.transpose',
    'grid.flip-h',
    'grid.flip-v',
    'grid.rotate',
    'grid.crop',
    'grid.slice-rows',
    'grid.slice-cols',
    'grid.splice-rows',
    'grid.splice-cols',
    'grid.concat-rows',
    'grid.concat-cols',
    'grid.cell-map',
    'grid.cell-mapi',
    'grid.cell-reduce',
    'grid.cell-reducei',
    'grid.push-rows',
    'grid.unshift-rows',
    'grid.pop-row',
    'grid.shift-row',
    'grid.push-cols',
    'grid.unshift-cols',
    'grid.pop-col',
    'grid.shift-col',
    'grid.from-array',
  ] as const,
  matrix: [
    'matrix.mul',
    'matrix.det',
    'matrix.inv',
    'matrix.adj',
    'matrix.cofactor',
    'matrix.minor',
    'matrix.trace',
    'matrix.symmetric?',
    'matrix.triangular?',
    'matrix.upper-triangular?',
    'matrix.lower-triangular?',
    'matrix.diagonal?',
    'matrix.square?',
    'matrix.orthogonal-matrix?',
    'matrix.identity?',
    'matrix.invertible?',
    'matrix.hilbert',
    'matrix.vandermonde',
    'matrix.band',
    'matrix.banded?',
    'matrix.rank',
    'matrix.frobenius-norm',
    'matrix.one-norm',
    'matrix.inf-norm',
    'matrix.max-norm',
  ] as const,
  vector: [
    'sum',
    'mean',
    'median',
    'prod',
  ] as const,
  vectorUtils: [
    'vector.monotonic?',
    'vector.strictly-monotonic?',
    'vector.increasing?',
    'vector.decreasing?',
    'vector.strictly-increasing?',
    'vector.strictly-decreasing?',
    'vector.mode',
    'vector.min-index',
    'vector.max-index',
    'vector.sort-indices',
    'vector.count-values',
    'vector.linspace',
    'vector.cumsum',
    'vector.cumprod',
    'vector.quartiles',
    'vector.percentile',
    'vector.quantile',
    'vector.histogram',
    'vector.ecdf',
    'vector.outliers?',
    'vector.outliers',
    'vector.bincount',
    'vector.winsorize',
    'vector.mse',
    'vector.mae',
    'vector.rmse',
    'vector.smape',
    ...getVectorMovingNames('mean'),
    ...getVectorMovingNames('median'),
    ...getVectorReductionNames('variance'),
    ...getVectorReductionNames('sample-variance'),
    ...getVectorMovingNames('sum'),
    ...getVectorMovingNames('prod'),
    ...getVectorReductionNames('stdev'),
    ...getVectorReductionNames('sample-stdev'),
    ...getVectorReductionNames('iqr'),
    ...getVectorReductionNames('span'),
    ...getVectorReductionNames('geometric-mean'),
    ...getVectorReductionNames('harmonic-mean'),
    ...getVectorReductionNames('skewness'),
    ...getVectorReductionNames('sample-skewness'),
    ...getVectorReductionNames('kurtosis'),
    ...getVectorReductionNames('sample-kurtosis'),
    ...getVectorReductionNames('excess-kurtosis'),
    ...getVectorReductionNames('sample-excess-kurtosis'),
    ...getVectorReductionNames('rms'),
    ...getVectorReductionNames('mad'),
    ...getVectorReductionNames('medad'),
    ...getVectorReductionNames('gini-coefficient'),
    ...getVectorReductionNames('entropy'),
    ...getVectorReductionNames('skewness'),
  ] as const,
  linAlg: [
    'linear-algebra.reflect',
    'linear-algebra.refract',
    'linear-algebra.lerp',
    'linear-algebra.rotate2d',
    'linear-algebra.rotate3d',
    'linear-algebra.dot',
    'linear-algebra.cross',
    'linear-algebra.normalize-minmax',
    'linear-algebra.normalize-zscore',
    'linear-algebra.normalize-robust',
    'linear-algebra.normalize-l1',
    'linear-algebra.normalize-l2',
    'linear-algebra.normalize-log',
    'linear-algebra.angle',
    'linear-algebra.projection',
    'linear-algebra.orthogonal?',
    'linear-algebra.parallel?',
    'linear-algebra.collinear?',
    'linear-algebra.cosine-similarity',
    'linear-algebra.euclidean-distance',
    'linear-algebra.euclidean-norm',
    'linear-algebra.manhattan-distance',
    'linear-algebra.manhattan-norm',
    'linear-algebra.hamming-distance',
    'linear-algebra.hamming-norm',
    'linear-algebra.chebyshev-distance',
    'linear-algebra.chebyshev-norm',
    'linear-algebra.minkowski-distance',
    'linear-algebra.minkowski-norm',
    'linear-algebra.cov',
    'linear-algebra.corr',
    'linear-algebra.spearman-corr',
    'linear-algebra.pearson-corr',
    'linear-algebra.kendall-tau',
    'linear-algebra.autocorrelation',
    'linear-algebra.cross-correlation',
    'linear-algebra.rref',
    'linear-algebra.solve',
    'linear-algebra.to-polar',
    'linear-algebra.from-polar',
  ] as const,
  numberTheory: [
    ...getNumberTheorySequenceNames('abundant'),
    ...getNumberTheorySequenceNames('arithmetic'),
    ...getNumberTheorySequenceNames('bell'),
    ...getNumberTheorySequenceNames('catalan'),
    ...getNumberTheorySequenceNames('composite'),
    ...getNumberTheorySequenceNames('deficient'),
    ...getNumberTheorySequenceNames('factorial'),
    ...getNumberTheorySequenceNames('fibonacci'),
    ...getNumberTheorySequenceNames('geometric'),
    ...getNumberTheorySequenceNames('golomb'),
    ...getNumberTheorySequenceNames('happy'),
    ...getNumberTheorySequenceNames('look-and-say'),
    ...getNumberTheorySequenceNames('lucas'),
    ...getNumberTheorySequenceNames('lucky'),
    ...getNumberTheorySequenceNames('mersenne'),
    ...getNumberTheorySequenceNames('padovan'),
    ...getNumberTheorySequenceNames('partition'),
    ...getNumberTheorySequenceNames('pell'),
    ...getNumberTheorySequenceNames('perfect'),
    ...getNumberTheorySequenceNames('perfect-cube'),
    ...getNumberTheorySequenceNames('perfect-power'),
    ...getNumberTheorySequenceNames('perfect-square'),
    ...getNumberTheorySequenceNames('polygonal'),
    ...getNumberTheorySequenceNames('prime'),
    ...getNumberTheorySequenceNames('recaman'),
    ...getNumberTheorySequenceNames('sylvester'),
    ...getNumberTheorySequenceNames('thue-morse'),
    ...getNumberTheorySequenceNames('tribonacci'),
    'number-theory.collatz-seq',
    'number-theory.juggler-seq',
    'number-theory.bernoulli-seq',
    'number-theory.bernoulli-take-while',
    'number-theory.bernoulli-nth',
    'number-theory.combinations',
    'number-theory.count-combinations',
    'number-theory.derangements',
    'number-theory.count-derangements',
    'number-theory.divisors',
    'number-theory.count-divisors',
    'number-theory.proper-divisors',
    'number-theory.count-proper-divisors',
    'number-theory.prime-factors',
    'number-theory.count-prime-factors',
    'number-theory.distinct-prime-factors',
    'number-theory.count-distinct-prime-factors',
    'number-theory.factorial',
    'number-theory.partitions',
    'number-theory.count-partitions',
    'number-theory.permutations',
    'number-theory.count-permutations',
    'number-theory.power-set',
    'number-theory.count-power-set',
    'number-theory.coprime?',
    'number-theory.divisible-by?',
    'number-theory.gcd',
    'number-theory.lcm',
    'number-theory.multinomial',
    'number-theory.amicable?',
    'number-theory.euler-totient',
    'number-theory.mobius',
    'number-theory.mertens',
    'number-theory.sigma',
    'number-theory.carmichael-lambda',
    'number-theory.cartesian-product',
    'number-theory.perfect-power',
    'number-theory.mod-exp',
    'number-theory.mod-inv',
    'number-theory.extended-gcd',
    'number-theory.chinese-remainder',
    'number-theory.stirling-first',
    'number-theory.stirling-second',
  ] as const,
  random: [
    'random.random!',
    'random.random-int!',
    'random.random-int-inclusive!',
    'random.random-float!',
    'random.random-boolean!',
    'random.random-item!',
    'random.random-sample!',
    'random.random-sample-unique!',
    'random.shuffle!',
    'random.random-normal!',
    'random.random-exponential!',
    'random.random-binomial!',
    'random.random-poisson!',
    'random.random-gamma!',
    'random.random-pareto!',
    'random.uuid!',
    'random.random-char!',
    'random.random-string!',
    'random.random-id!',
    'random.random-color!',
  ] as const,
  convert: [
    'convert.angstrom->um',
    'convert.angstrom->mm',
    'convert.angstrom->cm',
    'convert.angstrom->m',
    'convert.angstrom->km',
    'convert.angstrom->in',
    'convert.angstrom->ft',
    'convert.angstrom->yd',
    'convert.angstrom->mi',
    'convert.angstrom->nmi',
    'convert.um->angstrom',
    'convert.um->mm',
    'convert.um->cm',
    'convert.um->m',
    'convert.um->km',
    'convert.um->in',
    'convert.um->ft',
    'convert.um->yd',
    'convert.um->mi',
    'convert.um->nmi',
    'convert.mm->angstrom',
    'convert.mm->um',
    'convert.mm->cm',
    'convert.mm->m',
    'convert.mm->km',
    'convert.mm->in',
    'convert.mm->ft',
    'convert.mm->yd',
    'convert.mm->mi',
    'convert.mm->nmi',
    'convert.cm->angstrom',
    'convert.cm->um',
    'convert.cm->mm',
    'convert.cm->m',
    'convert.cm->km',
    'convert.cm->in',
    'convert.cm->ft',
    'convert.cm->yd',
    'convert.cm->mi',
    'convert.cm->nmi',
    'convert.m->angstrom',
    'convert.m->um',
    'convert.m->mm',
    'convert.m->cm',
    'convert.m->km',
    'convert.m->in',
    'convert.m->ft',
    'convert.m->yd',
    'convert.m->mi',
    'convert.m->nmi',
    'convert.km->angstrom',
    'convert.km->um',
    'convert.km->mm',
    'convert.km->cm',
    'convert.km->m',
    'convert.km->in',
    'convert.km->ft',
    'convert.km->yd',
    'convert.km->mi',
    'convert.km->nmi',
    'convert.in->angstrom',
    'convert.in->um',
    'convert.in->mm',
    'convert.in->cm',
    'convert.in->m',
    'convert.in->km',
    'convert.in->ft',
    'convert.in->yd',
    'convert.in->mi',
    'convert.in->nmi',
    'convert.ft->angstrom',
    'convert.ft->um',
    'convert.ft->mm',
    'convert.ft->cm',
    'convert.ft->m',
    'convert.ft->km',
    'convert.ft->in',
    'convert.ft->yd',
    'convert.ft->mi',
    'convert.ft->nmi',
    'convert.yd->angstrom',
    'convert.yd->um',
    'convert.yd->mm',
    'convert.yd->cm',
    'convert.yd->m',
    'convert.yd->km',
    'convert.yd->in',
    'convert.yd->ft',
    'convert.yd->mi',
    'convert.yd->nmi',
    'convert.mi->angstrom',
    'convert.mi->um',
    'convert.mi->mm',
    'convert.mi->cm',
    'convert.mi->m',
    'convert.mi->km',
    'convert.mi->in',
    'convert.mi->ft',
    'convert.mi->yd',
    'convert.mi->nmi',
    'convert.nmi->angstrom',
    'convert.nmi->um',
    'convert.nmi->mm',
    'convert.nmi->cm',
    'convert.nmi->m',
    'convert.nmi->km',
    'convert.nmi->in',
    'convert.nmi->ft',
    'convert.nmi->yd',
    'convert.nmi->mi',
    'convert.mg->g',
    'convert.mg->kg',
    'convert.mg->t',
    'convert.mg->oz',
    'convert.mg->lb',
    'convert.mg->st',
    'convert.g->mg',
    'convert.g->kg',
    'convert.g->t',
    'convert.g->oz',
    'convert.g->lb',
    'convert.g->st',
    'convert.kg->mg',
    'convert.kg->g',
    'convert.kg->t',
    'convert.kg->oz',
    'convert.kg->lb',
    'convert.kg->st',
    'convert.t->mg',
    'convert.t->g',
    'convert.t->kg',
    'convert.t->oz',
    'convert.t->lb',
    'convert.t->st',
    'convert.oz->mg',
    'convert.oz->g',
    'convert.oz->kg',
    'convert.oz->t',
    'convert.oz->lb',
    'convert.oz->st',
    'convert.lb->mg',
    'convert.lb->g',
    'convert.lb->kg',
    'convert.lb->t',
    'convert.lb->oz',
    'convert.lb->st',
    'convert.st->mg',
    'convert.st->g',
    'convert.st->kg',
    'convert.st->t',
    'convert.st->oz',
    'convert.st->lb',
    'convert.ml->cl',
    'convert.ml->dl',
    'convert.ml->l',
    'convert.ml->tsp',
    'convert.ml->tbsp',
    'convert.ml->fl-oz',
    'convert.ml->cup',
    'convert.ml->pt',
    'convert.ml->qt',
    'convert.ml->gal',
    'convert.cl->ml',
    'convert.cl->dl',
    'convert.cl->l',
    'convert.cl->tsp',
    'convert.cl->tbsp',
    'convert.cl->fl-oz',
    'convert.cl->cup',
    'convert.cl->pt',
    'convert.cl->qt',
    'convert.cl->gal',
    'convert.dl->ml',
    'convert.dl->cl',
    'convert.dl->l',
    'convert.dl->tsp',
    'convert.dl->tbsp',
    'convert.dl->fl-oz',
    'convert.dl->cup',
    'convert.dl->pt',
    'convert.dl->qt',
    'convert.dl->gal',
    'convert.l->ml',
    'convert.l->cl',
    'convert.l->dl',
    'convert.l->tsp',
    'convert.l->tbsp',
    'convert.l->fl-oz',
    'convert.l->cup',
    'convert.l->pt',
    'convert.l->qt',
    'convert.l->gal',
    'convert.tsp->ml',
    'convert.tsp->cl',
    'convert.tsp->dl',
    'convert.tsp->l',
    'convert.tsp->tbsp',
    'convert.tsp->fl-oz',
    'convert.tsp->cup',
    'convert.tsp->pt',
    'convert.tsp->qt',
    'convert.tsp->gal',
    'convert.tbsp->ml',
    'convert.tbsp->cl',
    'convert.tbsp->dl',
    'convert.tbsp->l',
    'convert.tbsp->tsp',
    'convert.tbsp->fl-oz',
    'convert.tbsp->cup',
    'convert.tbsp->pt',
    'convert.tbsp->qt',
    'convert.tbsp->gal',
    'convert.fl-oz->ml',
    'convert.fl-oz->cl',
    'convert.fl-oz->dl',
    'convert.fl-oz->l',
    'convert.fl-oz->tsp',
    'convert.fl-oz->tbsp',
    'convert.fl-oz->cup',
    'convert.fl-oz->pt',
    'convert.fl-oz->qt',
    'convert.fl-oz->gal',
    'convert.cup->ml',
    'convert.cup->cl',
    'convert.cup->dl',
    'convert.cup->l',
    'convert.cup->tsp',
    'convert.cup->tbsp',
    'convert.cup->fl-oz',
    'convert.cup->pt',
    'convert.cup->qt',
    'convert.cup->gal',
    'convert.pt->ml',
    'convert.pt->cl',
    'convert.pt->dl',
    'convert.pt->l',
    'convert.pt->tsp',
    'convert.pt->tbsp',
    'convert.pt->fl-oz',
    'convert.pt->cup',
    'convert.pt->qt',
    'convert.pt->gal',
    'convert.qt->ml',
    'convert.qt->cl',
    'convert.qt->dl',
    'convert.qt->l',
    'convert.qt->tsp',
    'convert.qt->tbsp',
    'convert.qt->fl-oz',
    'convert.qt->cup',
    'convert.qt->pt',
    'convert.qt->gal',
    'convert.gal->ml',
    'convert.gal->cl',
    'convert.gal->dl',
    'convert.gal->l',
    'convert.gal->tsp',
    'convert.gal->tbsp',
    'convert.gal->fl-oz',
    'convert.gal->cup',
    'convert.gal->pt',
    'convert.gal->qt',
    'convert.ms->s',
    'convert.ms->min',
    'convert.ms->h',
    'convert.ms->day',
    'convert.ms->week',
    'convert.s->ms',
    'convert.s->min',
    'convert.s->h',
    'convert.s->day',
    'convert.s->week',
    'convert.min->ms',
    'convert.min->s',
    'convert.min->h',
    'convert.min->day',
    'convert.min->week',
    'convert.h->ms',
    'convert.h->s',
    'convert.h->min',
    'convert.h->day',
    'convert.h->week',
    'convert.day->ms',
    'convert.day->s',
    'convert.day->min',
    'convert.day->h',
    'convert.day->week',
    'convert.week->ms',
    'convert.week->s',
    'convert.week->min',
    'convert.week->h',
    'convert.week->day',
    'convert.mm2->cm2',
    'convert.mm2->m2',
    'convert.mm2->km2',
    'convert.mm2->in2',
    'convert.mm2->ft2',
    'convert.mm2->yd2',
    'convert.mm2->acre',
    'convert.mm2->hectare',
    'convert.cm2->mm2',
    'convert.cm2->m2',
    'convert.cm2->km2',
    'convert.cm2->in2',
    'convert.cm2->ft2',
    'convert.cm2->yd2',
    'convert.cm2->acre',
    'convert.cm2->hectare',
    'convert.m2->mm2',
    'convert.m2->cm2',
    'convert.m2->km2',
    'convert.m2->in2',
    'convert.m2->ft2',
    'convert.m2->yd2',
    'convert.m2->acre',
    'convert.m2->hectare',
    'convert.km2->mm2',
    'convert.km2->cm2',
    'convert.km2->m2',
    'convert.km2->in2',
    'convert.km2->ft2',
    'convert.km2->yd2',
    'convert.km2->acre',
    'convert.km2->hectare',
    'convert.in2->mm2',
    'convert.in2->cm2',
    'convert.in2->m2',
    'convert.in2->km2',
    'convert.in2->ft2',
    'convert.in2->yd2',
    'convert.in2->acre',
    'convert.in2->hectare',
    'convert.ft2->mm2',
    'convert.ft2->cm2',
    'convert.ft2->m2',
    'convert.ft2->km2',
    'convert.ft2->in2',
    'convert.ft2->yd2',
    'convert.ft2->acre',
    'convert.ft2->hectare',
    'convert.yd2->mm2',
    'convert.yd2->cm2',
    'convert.yd2->m2',
    'convert.yd2->km2',
    'convert.yd2->in2',
    'convert.yd2->ft2',
    'convert.yd2->acre',
    'convert.yd2->hectare',
    'convert.acre->mm2',
    'convert.acre->cm2',
    'convert.acre->m2',
    'convert.acre->km2',
    'convert.acre->in2',
    'convert.acre->ft2',
    'convert.acre->yd2',
    'convert.acre->hectare',
    'convert.hectare->mm2',
    'convert.hectare->cm2',
    'convert.hectare->m2',
    'convert.hectare->km2',
    'convert.hectare->in2',
    'convert.hectare->ft2',
    'convert.hectare->yd2',
    'convert.hectare->acre',
    'convert.m/s->km/h',
    'convert.m/s->mph',
    'convert.m/s->kn',
    'convert.m/s->ft/s',
    'convert.km/h->m/s',
    'convert.km/h->mph',
    'convert.km/h->kn',
    'convert.km/h->ft/s',
    'convert.mph->m/s',
    'convert.mph->km/h',
    'convert.mph->kn',
    'convert.mph->ft/s',
    'convert.kn->m/s',
    'convert.kn->km/h',
    'convert.kn->mph',
    'convert.kn->ft/s',
    'convert.ft/s->m/s',
    'convert.ft/s->km/h',
    'convert.ft/s->mph',
    'convert.ft/s->kn',
    'convert.b->kb',
    'convert.b->mb',
    'convert.b->gb',
    'convert.b->tb',
    'convert.b->pb',
    'convert.kb->b',
    'convert.kb->mb',
    'convert.kb->gb',
    'convert.kb->tb',
    'convert.kb->pb',
    'convert.mb->b',
    'convert.mb->kb',
    'convert.mb->gb',
    'convert.mb->tb',
    'convert.mb->pb',
    'convert.gb->b',
    'convert.gb->kb',
    'convert.gb->mb',
    'convert.gb->tb',
    'convert.gb->pb',
    'convert.tb->b',
    'convert.tb->kb',
    'convert.tb->mb',
    'convert.tb->gb',
    'convert.tb->pb',
    'convert.pb->b',
    'convert.pb->kb',
    'convert.pb->mb',
    'convert.pb->gb',
    'convert.pb->tb',
    'convert.pa->kpa',
    'convert.pa->bar',
    'convert.pa->atm',
    'convert.pa->psi',
    'convert.pa->mmhg',
    'convert.kpa->pa',
    'convert.kpa->bar',
    'convert.kpa->atm',
    'convert.kpa->psi',
    'convert.kpa->mmhg',
    'convert.bar->pa',
    'convert.bar->kpa',
    'convert.bar->atm',
    'convert.bar->psi',
    'convert.bar->mmhg',
    'convert.atm->pa',
    'convert.atm->kpa',
    'convert.atm->bar',
    'convert.atm->psi',
    'convert.atm->mmhg',
    'convert.psi->pa',
    'convert.psi->kpa',
    'convert.psi->bar',
    'convert.psi->atm',
    'convert.psi->mmhg',
    'convert.mmhg->pa',
    'convert.mmhg->kpa',
    'convert.mmhg->bar',
    'convert.mmhg->atm',
    'convert.mmhg->psi',
    'convert.j->kj',
    'convert.j->cal',
    'convert.j->kcal',
    'convert.j->wh',
    'convert.j->kwh',
    'convert.j->btu',
    'convert.kj->j',
    'convert.kj->cal',
    'convert.kj->kcal',
    'convert.kj->wh',
    'convert.kj->kwh',
    'convert.kj->btu',
    'convert.cal->j',
    'convert.cal->kj',
    'convert.cal->kcal',
    'convert.cal->wh',
    'convert.cal->kwh',
    'convert.cal->btu',
    'convert.kcal->j',
    'convert.kcal->kj',
    'convert.kcal->cal',
    'convert.kcal->wh',
    'convert.kcal->kwh',
    'convert.kcal->btu',
    'convert.wh->j',
    'convert.wh->kj',
    'convert.wh->cal',
    'convert.wh->kcal',
    'convert.wh->kwh',
    'convert.wh->btu',
    'convert.kwh->j',
    'convert.kwh->kj',
    'convert.kwh->cal',
    'convert.kwh->kcal',
    'convert.kwh->wh',
    'convert.kwh->btu',
    'convert.btu->j',
    'convert.btu->kj',
    'convert.btu->cal',
    'convert.btu->kcal',
    'convert.btu->wh',
    'convert.btu->kwh',
    'convert.w->kw',
    'convert.w->mw',
    'convert.w->hp',
    'convert.kw->w',
    'convert.kw->mw',
    'convert.kw->hp',
    'convert.mw->w',
    'convert.mw->kw',
    'convert.mw->hp',
    'convert.hp->w',
    'convert.hp->kw',
    'convert.hp->mw',
    'convert.hz->khz',
    'convert.hz->mhz',
    'convert.hz->ghz',
    'convert.khz->hz',
    'convert.khz->mhz',
    'convert.khz->ghz',
    'convert.mhz->hz',
    'convert.mhz->khz',
    'convert.mhz->ghz',
    'convert.ghz->hz',
    'convert.ghz->khz',
    'convert.ghz->mhz',
    'convert.deg->rad',
    'convert.deg->grad',
    'convert.deg->turn',
    'convert.rad->deg',
    'convert.rad->grad',
    'convert.rad->turn',
    'convert.grad->deg',
    'convert.grad->rad',
    'convert.grad->turn',
    'convert.turn->deg',
    'convert.turn->rad',
    'convert.turn->grad',
    'convert.c->f',
    'convert.c->k',
    'convert.f->c',
    'convert.f->k',
    'convert.k->c',
    'convert.k->f',
  ] as const,
  shorthand: [
    '-short-regexp',
    '-short-fn',
  ] as const satisfies `-short-${string}`[],
  datatype: [
    '-type-number',
    '-type-string',
    '-type-object',
    '-type-array',
    '-type-vector',
    '-type-matrix',
    '-type-grid',
    '-type-boolean',
    '-type-function',
    '-type-integer',
    '-type-any',
    '-type-null',
    '-type-collection',
    '-type-sequence',
    '-type-regexp',
    '-type-never',
  ] as const satisfies `-type-${string}`[],
} as const

export type CollectionApiName = typeof api.collection[number]
export type ArrayApiName = typeof api.array[number]
export type SequenceApiName = typeof api.sequence[number]
export type MathApiName = typeof api.math[number]
export type FunctionalApiName = typeof api.functional[number]
export type MiscApiName = typeof api.misc[number]
export type MetaApiName = typeof api.meta[number]
export type ObjectApiName = typeof api.object[number]
export type PredicateApiName = typeof api.predicate[number]
export type RegularExpressionApiName = typeof api.regularExpression[number]
export type SpecialExpressionsApiName = string
export type StringApiName = typeof api.string[number]
export type StringUtilsApiName = typeof api.stringUtils[number]
export type CollectionUtilsApiName = typeof api.collectionUtils[number]
export type SequenceUtilsApiName = typeof api.sequenceUtils[number]
export type BitwiseApiName = typeof api.bitwise[number]
export type BitwiseUtilsApiName = typeof api.bitwiseUtils[number]
export type AssertionApiName = typeof api.assertion[number]
export type AssertionUtilsApiName = typeof api.assertionUtils[number]
export type GridApiName = typeof api.grid[number]
export type MatrixApiName = typeof api.matrix[number]
export type NumberTheoryApiName = typeof api.numberTheory[number]
export type VectorApiName = typeof api.vector[number]
export type VectorUtilsApiName = typeof api.vectorUtils[number]
export type LinAlgApiName = typeof api.linAlg[number]
export type RandomApiName = typeof api.random[number]
export type MathUtilsApiName = typeof api.mathUtils[number]
export type FunctionalUtilsApiName = typeof api.functionalUtils[number]
export type ConvertApiName = typeof api.convert[number]

// Core functions - always available without import
export type CoreNormalExpressionName =
  | CollectionApiName
  | ArrayApiName
  | SequenceApiName
  | MathApiName
  | FunctionalApiName
  | MetaApiName
  | MiscApiName
  | ObjectApiName
  | PredicateApiName
  | RegularExpressionApiName
  | StringApiName
  | BitwiseApiName
  | VectorApiName
  | AssertionApiName

// Module functions - require import()
export type ModuleExpressionName =
  | MatrixApiName
  | VectorUtilsApiName
  | LinAlgApiName
  | GridApiName
  | NumberTheoryApiName
  | RandomApiName
  | MathUtilsApiName
  | FunctionalUtilsApiName
  | AssertionUtilsApiName
  | StringUtilsApiName
  | CollectionUtilsApiName
  | SequenceUtilsApiName
  | BitwiseUtilsApiName
  | ConvertApiName
export type NormalExpressionName =
  | CoreNormalExpressionName
  | ModuleExpressionName

export type FunctionName =
  | NormalExpressionName
  | SpecialExpressionsApiName

export type ShorthandName = typeof api.shorthand[number]

export type DatatypeName = typeof api.datatype[number]

// Core API function names (always available)
const coreApiFunctionNames = [
  ...api.collection,
  ...api.array,
  ...api.sequence,
  ...api.math,
  ...api.functional,
  ...api.meta,
  ...api.misc,
  ...api.object,
  ...api.predicate,
  ...api.regularExpression,
  ...api.string,
  ...api.bitwise,
  ...api.vector,
  ...api.assertion,
] as const

// Module API function names (require import())
const moduleApiFunctionNames = [
  ...api.matrix,
  ...api.vectorUtils,
  ...api.linAlg,
  ...api.grid,
  ...api.numberTheory,
  ...api.random,
  ...api.mathUtils,
  ...api.functionalUtils,
  ...api.assertionUtils,
  ...api.stringUtils,
  ...api.collectionUtils,
  ...api.sequenceUtils,
  ...api.bitwiseUtils,
  ...api.convert,
] as const
const apiFunctionNames = [
  ...coreApiFunctionNames,
  ...moduleApiFunctionNames,
] as const

// Core API names (core functions + shorthand + datatype)
const coreApiNames = [
  ...coreApiFunctionNames,
  ...api.shorthand,
  ...api.datatype,
] as const

const apiNames = [
  ...apiFunctionNames,
  ...api.shorthand,
  ...api.datatype,
] as const

export type CoreApiName = typeof coreApiNames[number]
export type ApiName = typeof apiNames[number]

export function isApiName(arg: string): arg is ApiName {
  return apiNames.includes(arg as ApiName)
}

export function isCoreApiName(arg: string): arg is CoreApiName {
  return coreApiNames.includes(arg as CoreApiName)
}
